import { AnimatedNumber } from "@/components/defi/AnimatedNumber";
import {
	DISPLAY_MAX_LTV,
	LIQUIDATION_THRESHOLD,
	MIN_COLLATERAL_RATIO,
	getRiskZoneFromCr,
	maxLtvFromCr,
	riskZoneColor,
	type RiskZone,
} from "@/lib/defi-math";
import { RISK_STATUS_CLASSES, type VaultRiskBand } from "@/lib/vault-risk";
import { cn } from "@/lib/utils";
import { useMemo } from "react";

function zoneToBand(zone: RiskZone): VaultRiskBand {
	switch (zone) {
		case "safe":
			return "healthy";
		case "moderate":
			return "caution";
		case "danger":
			return "at_risk";
		case "liquidatable":
			return "liquidatable";
		case "underwater":
			return "underwater";
	}
}

function zoneLabel(zone: RiskZone): string {
	switch (zone) {
		case "safe":
			return "Healthy";
		case "moderate":
			return "Caution";
		case "danger":
			return "At risk";
		case "liquidatable":
			return "Liquidatable";
		case "underwater":
			return "Underwater";
	}
}

function zoneHint(
	zone: RiskZone,
	minCr: number,
	liqCr: number,
	cr: number,
): string {
	switch (zone) {
		case "safe":
			return Number.isFinite(cr)
				? `CR ${cr.toFixed(2)}× — comfortable above mint floor ${minCr.toFixed(2)}×.`
				: "No debt — fully collateralized.";
		case "moderate":
			return `Near mint floor ${minCr.toFixed(2)}× — watch before minting more.`;
		case "danger":
			return `Below mint floor ${minCr.toFixed(2)}× but above liq ${liqCr.toFixed(2)}× — add collateral or repay.`;
		case "liquidatable":
			return `CR below ${liqCr.toFixed(2)}× — fixed-spread liquidation open.`;
		case "underwater":
			return "CR below 1.00× — Dutch auction path only.";
	}
}

const SIZES = { sm: 112, md: 148, lg: 180 };

/**
 * Semicircle risk gauge. Health = CR = coll/debt (protocol HF).
 * Arc still tracks LTV; center can show Health (default) or LTV.
 */
export function RiskGauge({
	ltv,
	healthFactor,
	maxLtv = DISPLAY_MAX_LTV,
	liquidationCr = LIQUIDATION_THRESHOLD,
	mintCr = MIN_COLLATERAL_RATIO,
	className,
	size = "md",
	showLiqCr = true,
	centerMetric = "health",
}: {
	ltv: number;
	/** Must be CR = coll/debt (not maxLtv/ltv) */
	healthFactor: number;
	maxLtv?: number;
	liquidationCr?: number;
	mintCr?: number;
	className?: string;
	size?: "sm" | "md" | "lg";
	showLiqCr?: boolean;
	/** Vault cards: health. Borrow mint step can pass "ltv". */
	centerMetric?: "ltv" | "health";
}) {
	const dim = SIZES[size];
	const arcHeight = dim / 2 + 16;
	const liqLtv = maxLtvFromCr(liquidationCr);
	const mintLtv = maxLtvFromCr(mintCr);
	const scaleMax = Math.max(maxLtv, liqLtv * 1.05);
	const clamped = Math.min(scaleMax, Math.max(0, ltv));

	const cr =
		Number.isFinite(healthFactor) && healthFactor > 0
			? healthFactor
			: ltv > 0
				? 100 / ltv
				: Number.POSITIVE_INFINITY;

	const zone = getRiskZoneFromCr(cr, mintCr, liquidationCr);
	const band = zoneToBand(zone);
	const color = riskZoneColor(zone);
	const pct = scaleMax > 0 ? Math.min(1, clamped / scaleMax) : 0;
	const radius = (dim - 20) / 2;
	const circumference = Math.PI * radius;
	const offset = circumference * (1 - pct);
	const cx = dim / 2;
	const cy = dim / 2;
	const hfDisplay = Number.isFinite(healthFactor)
		? healthFactor
		: Number.POSITIVE_INFINITY;

	const mintPct = Math.min(1, mintLtv / scaleMax);
	const liqPctOnArc = Math.min(1, liqLtv / scaleMax);
	const label = useMemo(() => zoneLabel(zone), [zone]);
	const hint = useMemo(
		() => zoneHint(zone, mintCr, liquidationCr, cr),
		[zone, mintCr, liquidationCr, cr],
	);

	const polar = (angle: number, r: number) => ({
		x: cx + r * Math.cos(Math.PI - angle),
		y: cy - r * Math.sin(Math.PI - angle),
	});

	const mintPt = polar(Math.PI * mintPct, radius);
	const liqPt = polar(Math.PI * liqPctOnArc, radius);

	return (
		<div
			className={cn(
				"flex flex-col items-center gap-2 rounded-2xl p-3",
				RISK_STATUS_CLASSES[band].ringClass,
				className,
			)}
			role="img"
			aria-label={`Health ${
				hfDisplay === Number.POSITIVE_INFINITY ? "infinite" : hfDisplay.toFixed(2)
			}× CR, LTV ${clamped.toFixed(1)}%, ${label}. Mint floor ${mintCr.toFixed(2)}× · liquidate ${liquidationCr.toFixed(2)}×.`}
		>
			<span
				className="rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide"
				style={{
					color,
					backgroundColor: `${color}22`,
					border: `1px solid ${color}66`,
				}}
			>
				{label}
			</span>

			<svg
				width={dim}
				height={arcHeight}
				viewBox={`0 0 ${dim} ${arcHeight}`}
				className="overflow-visible"
				aria-hidden
			>
				<path
					d={`M ${cx - radius} ${cy} A ${radius} ${radius} 0 0 1 ${cx + radius} ${cy}`}
					fill="none"
					stroke="var(--bg-muted)"
					strokeWidth={12}
					strokeLinecap="round"
				/>
				<path
					d={`M ${cx - radius} ${cy} A ${radius} ${radius} 0 0 1 ${cx + radius} ${cy}`}
					fill="none"
					stroke={color}
					strokeWidth={12}
					strokeLinecap="round"
					strokeDasharray={circumference}
					strokeDashoffset={offset}
					style={{
						transition: "stroke-dashoffset 500ms ease, stroke 300ms ease",
					}}
				/>
				{/* Mint floor (orange) · liq marker (red) */}
				<circle cx={mintPt.x} cy={mintPt.y} r={3} fill="#F97316" />
				<circle cx={liqPt.x} cy={liqPt.y} r={3.5} fill="#DC2626" />
				<text
					x={cx}
					y={cy - 8}
					textAnchor="middle"
					className="fill-[var(--text-primary)]"
					style={{
						fontFamily: "var(--font-mono)",
						fontSize: size === "lg" ? 28 : size === "md" ? 24 : 20,
						fontWeight: 700,
					}}
				>
					{centerMetric === "health"
						? hfDisplay === Number.POSITIVE_INFINITY
							? "∞"
							: `${hfDisplay.toFixed(2)}×`
						: `${clamped.toFixed(1)}%`}
				</text>
				<text
					x={cx}
					y={cy + 12}
					textAnchor="middle"
					fill="var(--text-muted)"
					style={{ fontSize: 10, fontWeight: 600 }}
				>
					{centerMetric === "health" ? "Health" : "LTV"}
				</text>
			</svg>

			<div className="grid w-full max-w-[260px] grid-cols-3 gap-1.5 text-center text-[10px]">
				<div className="rounded-xl bg-muted/80 px-1.5 py-2">
					<p className="text-muted-foreground">
						{centerMetric === "health" ? "LTV" : "Health"}
					</p>
					<p className="font-mono-amount text-sm font-bold">
						{centerMetric === "health" ? (
							<>{clamped.toFixed(1)}%</>
						) : hfDisplay === Number.POSITIVE_INFINITY ? (
							"∞"
						) : (
							<AnimatedNumber value={hfDisplay} decimals={2} suffix="×" />
						)}
					</p>
				</div>
				<div className="rounded-xl bg-muted/80 px-1.5 py-2">
					<p className="text-muted-foreground">Mint</p>
					<p className="font-mono-amount text-sm font-bold">
						{mintCr.toFixed(2)}×
					</p>
				</div>
				<div className="rounded-xl bg-muted/80 px-1.5 py-2">
					<p className="text-muted-foreground">Liq. CR</p>
					<p className="font-mono-amount text-sm font-bold">
						{showLiqCr ? `${liquidationCr.toFixed(2)}×` : "—"}
					</p>
				</div>
			</div>
			<p className="max-w-[280px] text-center text-[11px] leading-snug text-muted-foreground">
				{hint}
			</p>
		</div>
	);
}
